import type { PlayerRatingResponse, ProfileRecruitResponse } from '@on3/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { On3Avatar } from 'components/Image/On3Avatar';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { format } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';

import styles from './RecruitModule.module.scss';

interface PlayerRankingsProps {
  playerRankings?: PlayerRatingResponse | null;
  recruitData?: ProfileRecruitResponse | null;
}

export const RecruitModule = ({
  playerRankings,
  recruitData,
}: PlayerRankingsProps) => {
  const { classYear, committedStatus, homeTown, highSchoolOrg } =
    recruitData || {};

  const {
    ranking,
    positionAbbr,
    consensusRating,
    consensusStars,
    consensusStateRank,
    consensusOverallRank,
    stateAbbr,
    consensusPositionRank,
    fiveStarPlus,
  } = playerRankings || {};

  const router = useRouter();
  const isScouting = router.asPath.toLowerCase().includes('/scouting/');
  const baseLink = `/rivals/rankings/industry-player/${slugify(
    ranking?.sport?.name,
  )}/${ranking?.year}/`;
  const linkPosition = positionAbbr?.toLocaleLowerCase() || 'qb';
  const linkState = stateAbbr?.toLocaleLowerCase() || 'al';
  const positionLink = `${baseLink}?position=${linkPosition}`;
  const stateLink = `${baseLink}?state=${linkState}`;
  const rankingYear = ranking?.year || new Date().getFullYear();

  return (
    <section className={styles.block}>
      <h2>As a Recruit</h2>
      <div>
        {consensusRating && (
          <>
            <div className={styles.row}>
              <span className={styles.label}>On3 Industry Rating</span>
              <div className={styles.ratings}>
                <StarRating
                  fiveStarPlus={fiveStarPlus}
                  href={baseLink}
                  large
                  sport={ranking?.sport?.name}
                  stars={consensusStars || 0}
                  year={rankingYear.toString()}
                />{' '}
                <span>{consensusRating?.toFixed(2)}</span>
                {highSchoolOrg?.defaultAsset && (
                  <figure className={styles.highSchool}>
                    <Link href={`/high-school/${highSchoolOrg.urlSlug}/`}>
                      <On3Avatar
                        domain={highSchoolOrg.defaultAsset.domain}
                        height={16}
                        source={highSchoolOrg.defaultAsset.source}
                        width={16}
                      />
                    </Link>
                  </figure>
                )}
              </div>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Rankings</span>
              <div className={styles.rankings}>
                <div className={styles.ranking}>
                  <span>Natl</span>
                  <Link href={baseLink}>{consensusOverallRank || '-'}</Link>
                </div>
                <div className={styles.ranking}>
                  <span>{positionAbbr}</span>
                  <Link href={positionLink}>
                    {consensusPositionRank || '-'}
                  </Link>
                </div>
                <div className={styles.ranking}>
                  <span>{stateAbbr}</span>
                  <Link href={stateLink}>{consensusStateRank || '-'}</Link>
                </div>
              </div>
            </div>
          </>
        )}
        <div className={styles.row}>
          <span className={styles.label}>Class</span>
          <span>{classYear}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Commitment</span>
          <div className={styles.committed}>
            {committedStatus?.committedAsset?.url && (
              <On3Avatar
                height={25}
                source={committedStatus?.committedAsset?.url}
                teamAvatar
                width={25}
              />
            )}
            <span>
              {committedStatus?.date
                ? format(new Date(committedStatus?.date), 'MM/dd/yy')
                : '-'}
            </span>
          </div>
        </div>
        {!isScouting && (
          <>
            <div className={styles.row}>
              <span className={styles.label}>High School</span>
              <Link
                className={styles.schoolLink}
                href={`/high-school/${highSchoolOrg?.urlSlug}/`}
              >
                {highSchoolOrg?.name || '-'}
              </Link>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Hometown</span>
              <span>{homeTown || '-'}</span>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
